import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import PDFPreview from './PDFPreview';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import Vditor from 'vditor';
import 'vditor/dist/index.css';
import './resizable-panels.css';
import { pdfApi } from '../../services/pdf';

// 更准确的组件名称：PDFContentWorkspace - 表示PDF预览和内容编辑的工作区
const PDFContentWorkspace = forwardRef(({ file, content: initialContent = '', onSave, fileId }, ref) => {
  const [leftPanelSize, setLeftPanelSize] = useState(45); // 默认左侧宽度45%
  const vditorRef = useRef(null);
  const editorContainerRef = useRef(null);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const isDestroyingRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const initialContentRef = useRef(initialContent);

  // 检查Vditor实例是否完全初始化
  const isVditorReady = () => {
    try {
      return vditorRef.current && 
             !isDestroyingRef.current &&
             typeof vditorRef.current.getValue === 'function';
    } catch (error) {
      console.error('检查编辑器就绪状态出错:', error);
      return false;
    }
  };

  // 刷新编辑器大小
  const refreshEditorSize = useCallback(() => {
    if (!isVditorReady()) return;
    
    try {
      const editorElement = document.querySelector('.vditor');
      if (editorElement) {
        editorElement.style.width = '100%';
        editorElement.style.height = '100%';
        
        // 调整内部编辑区域
        const contentElements = document.querySelectorAll('.vditor-content, .vditor-wysiwyg, .vditor-reset');
        contentElements.forEach(element => {
          if (element) {
            element.style.height = '100%';
          }
        });
      }
    } catch (error) {
      console.error('刷新编辑器大小失败:', error);
    }
  }, []);

  // 通过ref暴露方法给父组件
  useImperativeHandle(ref, () => ({
    // 获取编辑器内容
    getContent: () => {
      if (isVditorReady()) {
        try {
          return vditorRef.current.getValue();
        } catch (error) {
          console.error('获取编辑器内容失败:', error);
          return initialContentRef.current || '';
        }
      }
      return initialContentRef.current || '';
    },
    // 设置编辑器内容
    setContent: (content) => {
      if (isVditorReady()) {
        try {
          vditorRef.current.setValue(content);
        } catch (error) {
          console.error('设置编辑器内容失败:', error);
        }
      }
    }
  }), []);

  // 处理面板大小变化
  const handlePanelResize = (sizes) => {
    setLeftPanelSize(sizes[0]);
    // 保存用户的布局偏好
    localStorage.setItem('pdfWorkspaceLeftPanelSize', sizes[0]);

    // 调整编辑器大小
    if (isVditorReady()) {
      try {
        setTimeout(() => {
          refreshEditorSize();
        }, 200);
      } catch (error) {
        console.error('面板调整大小时调整编辑器失败:', error);
      }
    }
  };

  // 加载之前保存的布局偏好
  useEffect(() => {
    const savedSize = localStorage.getItem('pdfWorkspaceLeftPanelSize');
    if (savedSize) {
      setLeftPanelSize(Number(savedSize));
    }
  }, []);

  // 更新当前内容的引用
  useEffect(() => {
    initialContentRef.current = initialContent;
  }, [initialContent]);

  // 安全销毁编辑器
  const safeDestroyEditor = () => {
    try {
      if (vditorRef.current) {
        isDestroyingRef.current = true;
        
        // 断开MutationObserver连接
        try {
          if (vditorRef.current._headingButtonsObserver) {
            vditorRef.current._headingButtonsObserver.disconnect();
            vditorRef.current._headingButtonsObserver = null;
          }
        } catch (observerError) {
          console.warn('断开MutationObserver连接时出错:', observerError);
        }
        
        // 销毁编辑器
        try {
          if (vditorRef.current.element && 
              document.contains(vditorRef.current.element)) {
            vditorRef.current.destroy();
          }
        } catch (destroyError) {
          console.warn('尝试销毁编辑器时出错:', destroyError);
        }
        
        // 清理引用
        vditorRef.current = null;
        setIsEditorReady(false);
        isDestroyingRef.current = false;
      }
    } catch (error) {
      console.error('销毁编辑器时发生错误:', error);
      // 确保清理引用
      vditorRef.current = null;
      setIsEditorReady(false);
      isDestroyingRef.current = false;
    }
  };

  // 手动保存内容
  const handleManualSave = useCallback(() => {
    console.log('触发手动保存...');
    
    // 检查编辑器是否已准备好
    if (!isVditorReady()) {
      console.warn('编辑器未就绪，无法保存');
      
      // 如果有初始内容，也尝试保存
      if (initialContentRef.current && onSave) {
        console.log('使用初始内容进行保存');
        onSave(initialContentRef.current);
        setSaving(true);
        setTimeout(() => setSaving(false), 500);
        return;
      }
      return;
    }

    try {
      setSaving(true);
      let content;
      
      try {
        content = vditorRef.current.getValue();
      } catch (getValueError) {
        console.error('获取编辑器内容失败:', getValueError);
        content = initialContentRef.current || '';
      }
      
      if (onSave) {
        console.log('正在保存内容...');
        onSave(content);
      }
      
      setTimeout(() => {
        setSaving(false);
      }, 500);
    } catch (error) {
      console.error('手动保存失败:', error);
      setSaving(false);
    }
  }, [onSave, initialContentRef]);

  // 统计文档内容
  const handleContentStats = useCallback(() => {
    if (!isVditorReady()) {
      console.warn('编辑器未就绪，无法统计');
      return;
    }

    try {
      // 先保存内容
      handleManualSave();
      // 获取编辑器内容
      const content = vditorRef.current.getValue();
      // 解析内容并统计
      const stats = analyzeMarkdownContent(content);
      // 显示统计结果
      showStatsDialog(stats);
    } catch (error) {
      console.error('统计文档内容失败:', error);
    }
  }, [handleManualSave]);

  // 解析Markdown内容并统计
  const analyzeMarkdownContent = (content) => {
    // 结果对象
    const result = {
      chapters: [],
      totalQuestions: 0
    };
    
    // 按行分割内容
    const lines = content.split('\n');
    
    // 当前章节、知识点、题型
    let currentChapter = null;
    let currentKnowledgePoint = null;
    let currentQuestionType = null;
    
    // 匹配数字题号的正则表达式
    const questionPattern = /^\s*(\d+)[.．、][\s\S]/;
    
    // 逐行分析内容
    lines.forEach(line => {
      const trimmedLine = line.trim();
      
      // 检测标题层级
      if (trimmedLine.startsWith('# ')) {
        // 章节 (H1)
        currentChapter = {
          title: trimmedLine.substring(2).trim(),
          knowledgePoints: [],
          questionCount: 0
        };
        result.chapters.push(currentChapter);
        currentKnowledgePoint = null;
        currentQuestionType = null;
      } else if (trimmedLine.startsWith('## ') && currentChapter) {
        // 知识点 (H2)
        currentKnowledgePoint = {
          title: trimmedLine.substring(3).trim(),
          questionTypes: [],
          questionCount: 0
        };
        currentChapter.knowledgePoints.push(currentKnowledgePoint);
        currentQuestionType = null;
      } else if (trimmedLine.startsWith('### ') && currentKnowledgePoint) {
        // 题型 (H3)
        currentQuestionType = {
          title: trimmedLine.substring(4).trim(),
          questionCount: 0
        };
        currentKnowledgePoint.questionTypes.push(currentQuestionType);
      } else if (questionPattern.test(trimmedLine) && currentQuestionType) {
        // 检测到题目，增加计数
        currentQuestionType.questionCount++;
        currentKnowledgePoint.questionCount++;
        currentChapter.questionCount++;
        result.totalQuestions++;
      }
    });
    
    return result;
  };

  // 显示统计结果对话框
  const showStatsDialog = (stats) => {
    // 创建表格HTML
    let tableHtml = `
      <table style="width: 100%; border-collapse: collapse; margin-top: 15px;">
        <thead>
          <tr>
            <th style="border: 1px solid #ddd; padding: 8px; text-align: left; background-color: #f2f2f2;">章节</th>
            <th style="border: 1px solid #ddd; padding: 8px; text-align: left; background-color: #f2f2f2;">知识点</th>
            <th style="border: 1px solid #ddd; padding: 8px; text-align: left; background-color: #f2f2f2;">题型</th>
            <th style="border: 1px solid #ddd; padding: 8px; text-align: center; background-color: #f2f2f2;">题目数</th>
          </tr>
        </thead>
        <tbody>
    `;
    
    // 遍历统计结果并构建表格
    stats.chapters.forEach(chapter => {
      let isFirstChapterRow = true;
      const chapterRowSpan = chapter.knowledgePoints.reduce((acc, kp) => acc + kp.questionTypes.length, 0) || 1;
      
      if (chapter.knowledgePoints.length === 0) {
        // 没有知识点的章节
        tableHtml += `
          <tr>
            <td style="border: 1px solid #ddd; padding: 8px;">${chapter.title}</td>
            <td style="border: 1px solid #ddd; padding: 8px;"></td>
            <td style="border: 1px solid #ddd; padding: 8px;"></td>
            <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${chapter.questionCount}</td>
          </tr>
        `;
      } else {
        chapter.knowledgePoints.forEach(kp => {
          let isFirstKnowledgeRow = true;
          const kpRowSpan = kp.questionTypes.length || 1;
          
          if (kp.questionTypes.length === 0) {
            // 没有题型的知识点
            tableHtml += `
              <tr>
                ${isFirstChapterRow ? `<td rowspan="${chapterRowSpan}" style="border: 1px solid #ddd; padding: 8px;">${chapter.title}</td>` : ''}
                <td style="border: 1px solid #ddd; padding: 8px;">${kp.title}</td>
                <td style="border: 1px solid #ddd; padding: 8px;"></td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${kp.questionCount}</td>
              </tr>
            `;
            isFirstChapterRow = false;
          } else {
            kp.questionTypes.forEach(qt => {
              tableHtml += `
                <tr>
                  ${isFirstChapterRow ? `<td rowspan="${chapterRowSpan}" style="border: 1px solid #ddd; padding: 8px;">${chapter.title}</td>` : ''}
                  ${isFirstKnowledgeRow ? `<td rowspan="${kpRowSpan}" style="border: 1px solid #ddd; padding: 8px;">${kp.title}</td>` : ''}
                  <td style="border: 1px solid #ddd; padding: 8px;">${qt.title}</td>
                  <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${qt.questionCount}</td>
                </tr>
              `;
              isFirstChapterRow = false;
              isFirstKnowledgeRow = false;
            });
          }
        });
      }
    });
    
    tableHtml += `</tbody></table>`;
    
    // 创建模态框HTML
    const modalHtml = `
      <div style="max-height: 500px; overflow-y: auto; padding: 15px;">
        <h2 style="text-align: center; margin-bottom: 20px;">文档统计结果</h2>
        <div style="margin-bottom: 10px;"><strong>总题目数:</strong> ${stats.totalQuestions}</div>
        ${tableHtml}
      </div>
      <div style="padding: 15px; text-align: center; border-top: 1px solid #eee; margin-top: 10px;">
        <button id="stats-close-btn" style="padding: 8px 16px; background-color: #1890ff; color: white; border: none; border-radius: 4px; cursor: pointer;">关闭</button>
      </div>
    `;
    
    // 创建模态框背景和内容
    const modalOverlay = document.createElement('div');
    modalOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 9999;
    `;
    
    const modalContent = document.createElement('div');
    modalContent.style.cssText = `
      background-color: white;
      border-radius: 4px;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
      width: 80%;
      max-width: 800px;
      max-height: 90vh;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    `;
    
    modalContent.innerHTML = modalHtml;
    modalOverlay.appendChild(modalContent);
    document.body.appendChild(modalOverlay);
    
    // 添加关闭事件
    document.getElementById('stats-close-btn').addEventListener('click', () => {
      document.body.removeChild(modalOverlay);
    });
  };

  // 初始化Vditor编辑器
  useEffect(() => {
    // 清理函数引用，确保每次Effect运行都有新的清理函数
    let isMounted = true;
    
    // 确保有DOM元素且编辑器尚未初始化
    if (editorContainerRef.current && !vditorRef.current && isMounted) {
      // 创建Vditor实例前先重置ready状态
      setIsEditorReady(false);
      isDestroyingRef.current = false;
      
      try {
        // 创建唯一ID
        const editorId = `vditor-editor-${Date.now()}`;
        editorContainerRef.current.id = editorId;
        
        // 将编辑器引用和处理函数暴露到全局以便顶栏按钮调用
        window.vditorRef = vditorRef;
        window.handleContentStats = handleContentStats;
        window.onSaveCallback = onSave;
        
        // 创建标准工具栏按钮配置
        const standardButtons = [
          'headings',
          '|',
          'code', 'inline-code', 'table', '|',
          'upload', '|',
          'outline', 'export', 'edit-mode', 'both', 'preview'
        ];
        
        // 将标准按钮转换为对象配置（带tipPosition: 's'）
        const toolbar = standardButtons.map(btn => {
          if (btn === '|') return btn;
          if (typeof btn === 'object') return btn;
          return {
            name: btn,
            tipPosition: 's'
          };
        });
        
        // 添加自定义保存按钮
        const saveButton = {
          hotkey: '⌘S',
          name: 'save',
          tipPosition: 's',
          tip: '保存文档',
          className: 'vditor-save',
          icon: '<svg t="1743409534396" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="1487" width="200" height="200"><path d="M925.248 356.928l-258.176-258.176a64 64 0 0 0-45.248-18.752H144a64 64 0 0 0-64 64v736a64 64 0 0 0 64 64h736a64 64 0 0 0 64-64V402.176a64 64 0 0 0-18.752-45.248zM288 144h192V256H288V144z m448 736H288V736h448v144z m144 0H800V704a32 32 0 0 0-32-32H256a32 32 0 0 0-32 32v176H144v-736H224V288a32 32 0 0 0 32 32h256a32 32 0 0 0 32-32V144h77.824l258.176 258.176V880z" p-id="1488"></path></svg>',
          click: () => {
            if (vditorRef.current) {
              handleManualSave();
            }
          }
        };
        
        // 添加统计按钮
        const statsButton = {
          name: 'stats',
          tipPosition: 's',
          tip: '统计题目信息',
          className: 'vditor-stats',
          icon: '<svg viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" width="200" height="200"><path d="M888.32 792.576v63.488h-752.64v-63.488h752.64z m0-99.328v63.488h-752.64v-63.488h752.64z m-564.224-300.032h-62.464v335.872h62.464v-335.872z m188.416-188.416h-62.464v524.288h62.464v-524.288z m188.928 106.496h-62.464v417.792h62.464v-417.792z" fill="#000000" p-id="1994"></path></svg>',
          click: () => {
            if (vditorRef.current) {
              // 先保存
              handleManualSave();
              // 然后统计
              handleContentStats();
            }
          }
        };
        
        // 创建左侧工具栏：保存、统计、撤销、重做按钮
        const leftToolbar = [
          saveButton,
          statsButton,
          {
            name: 'undo',
            tipPosition: 's'
          },
          {
            name: 'redo',
            tipPosition: 's'
          }
        ];
        
        // 将左侧工具栏、分隔符和其他按钮组合
        const finalToolbar = [
          ...leftToolbar,
          '|',
          ...toolbar,
          '|',
          {
            name: 'fullscreen',
            tipPosition: 's'
          }
        ];
        
        // 根据官方推荐的初始化方式创建Vditor实例
        vditorRef.current = new Vditor(editorId, {
          height: '100%',
          width: '100%',
          mode: 'ir',
          placeholder: '请在此处编辑内容...',
          theme: 'light',
          icon: 'material',
          toolbar: finalToolbar,
          cache: {
            enable: false
          },
          preview: {
            hljs: {
              enable: true,
              style: 'github'
            }
          },
          outline: {
            enable: true,
            position: 'left',
            level: [1, 2, 3, 4, 5, 6],
            custom: {}, // 确保此处没有custom设置，避免冲突
            headingLabel: {
              1: '章节',
              2: '知识点',
              3: '题型',
              4: 'H4',
              5: 'H5',
              6: 'H6'
            }
          },
          upload: {
            url: '/api/upload/image',
            linkToImgUrl: '/api/upload/fetch',
            max: 10 * 1024 * 1024,
            accept: 'image/*',
            withCredentials: true,
            token: sessionStorage.getItem('token'),
            handler: (files) => {
              // 自定义上传
              if (!fileId) {
                alert('请先保存文档，再上传图片');
                return "上传中...";
              }

              const promises = [];
              
              for (let i = 0; i < files.length; i++) {
                const file = files[i];
                
                const promise = pdfApi.uploadImage(fileId, file)
                  .then(response => {
                    console.log("上传图片成功:", response);
                    // 从后端返回的结果中获取图片URL
                    let imageUrl = null;
                    
                    if (response && (response.success || response.code === 0)) {
                      // 提取图片URL的多种可能格式
                      if (response.data && response.data.succMap) {
                        const fileName = Object.keys(response.data.succMap)[0];
                        imageUrl = response.data.succMap[fileName];
                      } else if (response.data && response.data.url) {
                        imageUrl = response.data.url;
                      } else if (response.data && response.data.image_url) {
                        imageUrl = response.data.image_url;
                      } else if (typeof response.data === 'string') {
                        imageUrl = response.data;
                      }
                      
                      if (imageUrl) {
                        // 使用Markdown格式插入图片
                        const mdLink = `![${file.name}](${imageUrl})`;
                        vditorRef.current.insertValue(mdLink);
                        return true;
                      }
                    }
                    return false;
                  })
                  .catch(error => {
                    console.error("上传图片失败:", error);
                    alert(`图片 ${file.name} 上传失败，请重试`);
                    return false;
                  });
                
                promises.push(promise);
              }
              
              return "上传中...";
            },
          },
          after: () => {
            if (isMounted) {
              setIsEditorReady(true);
              
              console.log('编辑器初始化完成，添加自定义样式');
              
              // 添加样式 - 统一所有标题相关样式
              const styleElement = document.createElement('style');
              styleElement.textContent = `
                /* 编辑器中的标题样式 */
                h1.vditor-ir__node[data-block="0"]::before {
                  content: "章节" !important;
                  min-width: 50px !important;
                  display: inline-block !important;
                }
                
                h2.vditor-ir__node[data-block="0"]::before {
                  content: "知识点" !important;
                  min-width: 50px !important;
                  display: inline-block !important;
                }
                
                h3.vditor-ir__node[data-block="0"]::before {
                  content: "题型" !important;
                  min-width: 50px !important;
                  display: inline-block !important;
                }

                /* 大纲面板中的标题样式 */
                .vditor-outline span[data-type="heading-marker"][data-marker="#"]::before {
                  content: "章节" !important;
                  min-width: 50px !important;
                  display: inline-block !important;
                }
                
                .vditor-outline span[data-type="heading-marker"][data-marker="##"]::before {
                  content: "知识点" !important;
                  min-width: 50px !important;
                  display: inline-block !important;
                }
                
                .vditor-outline span[data-type="heading-marker"][data-marker="###"]::before {
                  content: "题型" !important;
                  min-width: 50px !important;
                  display: inline-block !important;
                }

                /* 确保样式优先级 */
                .vditor-outline span[data-type="heading-marker"]::before {
                  font-size: 12px !important;
                  color: var(--second-color, #4285f4) !important;
                  min-width: 50px !important;
                  display: inline-block !important;
                  margin-right: 8px !important;
                }

                /* 直接修改标题按钮文本 */
                button[data-tag="h1"] {
                  color: transparent !important;
                  position: relative !important;
                }
                button[data-tag="h1"]::before {
                  content: "章节" !important;
                  display: inline-block !important;
                  color: initial !important;
                  position: absolute !important;
                  left: 8px !important;
                }
                
                button[data-tag="h2"] {
                  color: transparent !important;
                  position: relative !important;
                }
                button[data-tag="h2"]::before {
                  content: "知识点" !important;
                  display: inline-block !important;
                  color: initial !important;
                  position: absolute !important;
                  left: 8px !important;
                }
                
                button[data-tag="h3"] {
                  color: transparent !important;
                  position: relative !important;
                }
                button[data-tag="h3"]::before {
                  content: "题型" !important;
                  display: inline-block !important;
                  color: initial !important;
                  position: absolute !important;
                  left: 8px !important;
                }
              `;
              document.head.appendChild(styleElement);
              
              setTimeout(() => {
                if (isMounted && vditorRef.current && initialContentRef.current) {
                  try {
                    vditorRef.current.setValue(initialContentRef.current);
                    console.log('成功设置编辑器内容');
                    
                    setTimeout(() => {
                      refreshEditorSize();
                    }, 300);
                  } catch (error) {
                    console.error('设置编辑器内容失败:', error);
                  }
                }
                
                try {
                  setTimeout(() => {
                    const saveBtn = document.querySelector('.vditor-save');
                    if (saveBtn) {
                      console.log('为保存按钮添加点击事件');
                      saveBtn.addEventListener('click', () => {
                        if (isMounted) {
                          handleManualSave();
                        }
                      });
                    }
                  }, 100);
                } catch (error) {
                  console.error('为保存按钮添加点击事件失败:', error);
                }
              }, 500);
            }
          }
        });

        // 添加保存按钮的样式和深色主题下的一些自定义样式
        const styleId = 'vditor-save-styles';
        if (!document.getElementById(styleId)) {
          const styleElement = document.createElement('style');
          styleElement.id = styleId;
          styleElement.textContent = `
            /* 确保所有提示向下显示 */
            .vditor-tooltipped__s:before,
            .vditor-tooltipped__s:after {
              top: 100% !important;
              bottom: auto !important;
            }
            .vditor-tooltipped__s:before {
              margin-top: -1px !important;
              margin-bottom: 0 !important;
            }
            .vditor-tooltipped__s:after {
              margin-top: 5px !important;
              margin-bottom: 0 !important;
            }
          `;
          document.head.appendChild(styleElement);
        }
      } catch (error) {
        console.error('初始化编辑器失败:', error);
        vditorRef.current = null;
      }
    }
    
    // 组件卸载时清理
    return () => {
      isMounted = false;
      safeDestroyEditor();
      // 清除全局对象引用
      window.vditorRef = undefined;
      window.handleContentStats = undefined;
      window.onSaveCallback = undefined;
    };
  }, [fileId, onSave, handleContentStats, handleManualSave, refreshEditorSize]);

  // 更新保存按钮状态
  useEffect(() => {
    if (isEditorReady && document.querySelector('.vditor-save')) {
      try {
        const saveBtn = document.querySelector('.vditor-save');
        if (saveBtn) {
          if (saving) {
            saveBtn.classList.add('saving');
          } else {
            saveBtn.classList.remove('saving');
          }
        }
      } catch (error) {
        console.error('更新保存按钮状态失败:', error);
      }
    }
  }, [saving, isEditorReady]);
  
  // 添加全局的保存按钮点击处理
  useEffect(() => {
    const handleGlobalClick = (event) => {
      // 检查点击的元素是否为保存按钮或其子元素
      let target = event.target;
      let isSaveButton = false;
      
      // 向上查找父元素，直到找到保存按钮或到达文档根节点
      while (target && !isSaveButton) {
        if (target.classList && target.classList.contains('vditor-save')) {
          isSaveButton = true;
          break;
        }
        target = target.parentNode;
      }
      
      // 如果点击的是保存按钮，则触发保存操作
      if (isSaveButton) {
        //console.log('全局监听器捕获到保存按钮点击');
        event.preventDefault();
        event.stopPropagation();
        handleManualSave();
      }
    };
    
    // 添加全局点击事件监听器
    document.addEventListener('click', handleGlobalClick, true);
    
    // 清理监听器
    return () => {
      document.removeEventListener('click', handleGlobalClick, true);
    };
  }, [handleManualSave]);

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <PanelGroup direction="horizontal" onLayout={handlePanelResize}>
        {/* PDF预览面板 */}
        <Panel id="pdf-preview" defaultSize={leftPanelSize} minSize={30}>
          <div style={{ height: '100%', width: '100%', overflow: 'hidden' }}>
            <PDFPreview file={file} mode="edit" />
          </div>
        </Panel>
        
        {/* 可调整大小的分隔线 */}
        <PanelResizeHandle className="resize-handle">
          <div className="handle-bar" />
        </PanelResizeHandle>
        
        {/* Vditor编辑器面板 */}
        <Panel id="text-editor" minSize={30}>
          <div style={{ height: '100%', width: '100%', overflow: 'hidden', background: '#1e1e1e' }}>
            <div 
              ref={editorContainerRef}
              style={{ 
                height: '100%', 
                width: '100%', 
                border: '0px solid #333',
                borderRadius: '4px',
                background: '#1e1e1e'
              }}
            />
            {!isEditorReady && (
              <div style={{
                position: 'absolute',
                top: '50%', 
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: '#1890ff',
                background: 'rgba(30, 30, 30, 0.7)',
                padding: '10px 20px',
                borderRadius: '4px'
              }}>
                编辑器加载中...
              </div>
            )}
          </div>
        </Panel>
      </PanelGroup>
    </div>
  );
});

export default PDFContentWorkspace; 